import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, TextField, Typography, Chip,
} from '@mui/material';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import { useTranslation } from 'react-i18next';
import {
  fetchEvohomeAccount,
  evohomeConnect,
  evohomeDisconnectAccount,
  evohomePurgeAccount,
  evohomeSync,
  fetchEvohomeAdminSummary,
  type EvohomeDevice,
  type EvohomeSystem,
} from '../../services/evohomeApi';
import { eventBus } from '../../services/EventBus';
import EvohomeSystemControls from './EvohomeSystemControls';
import {
  evohomeSetpointModeColor,
  formatEvohomeSetpointModeLabel,
} from './evohomeModeUtils';

const ACCENT = '#2391FF';
const HEAT = '#FF8A65';

interface Props {
  isDemoUser?: boolean;
  onDemoActionAttempt?: () => void;
  onGoToDevices?: () => void;
  onOpenZoneModal?: (device: EvohomeDevice) => void;
}

const EvohomeDiscoveryTab: React.FC<Props> = ({
  isDemoUser,
  onDemoActionAttempt,
  onGoToDevices,
  onOpenZoneModal,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [account, setAccount] = useState<any>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [syncResult, setSyncResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [unassigned, setUnassigned] = useState(0);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [zones, setZones] = useState<EvohomeDevice[]>([]);
  const [systemControlsKey, setSystemControlsKey] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const [acc, sum] = await Promise.all([
        fetchEvohomeAccount().catch(() => null),
        fetchEvohomeAdminSummary().catch(() => ({ total: 0, unassigned_count: 0 })),
      ]);
      setAccount(acc);
      setUnassigned(sum.unassigned_count || 0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const blockDemo = () => {
    if (!isDemoUser) return false;
    onDemoActionAttempt?.();
    return true;
  };

  const handleConnect = async () => {
    if (blockDemo()) return;
    if (!username.trim() || !password) return;
    setBusy(true);
    setError(null);
    try {
      const acc = await evohomeConnect(username.trim(), password);
      setAccount(acc?.connected != null ? acc : { connected: true, username: username.trim() });
      setPassword('');
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('settings.evohomeDiscovery.errorConnect', {
        defaultValue: 'Usuario/contraseña inválidos o error de conexión',
      }));
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (blockDemo()) return;
    if (!window.confirm(t('settings.evohomeDiscovery.disconnectConfirm', {
      defaultValue: '¿Desconectar cuenta Total Connect Comfort?',
    }))) return;
    setBusy(true);
    try {
      await evohomeDisconnectAccount();
      setAccount(null);
      setSyncResult(null);
      setSelectedLocationId(null);
      setZones([]);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const handlePurge = async () => {
    if (blockDemo()) return;
    if (!window.confirm(t('settings.evohomeDiscovery.purgeConfirm', {
      defaultValue: 'Se borran usuario, contraseña y tokens. Para volver a conectar hay que cargarlos de nuevo. Las zonas no se borran.',
    }))) return;
    setBusy(true);
    try {
      await evohomePurgeAccount();
      setAccount(null);
      setSyncResult(null);
      setSelectedLocationId(null);
      setZones([]);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const handleSync = async () => {
    if (blockDemo()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await evohomeSync();
      setSyncResult(res);
      eventBus.emit('estructura:refrescar');
      await refresh();
      setSystemControlsKey((k) => k + 1);
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'object' ? (d.message || JSON.stringify(d)) : (d || 'Error sync'));
    } finally {
      setBusy(false);
    }
  };

  const handleOverviewChange = useCallback((data: {
    locationId: number | null;
    systems: EvohomeSystem[];
    zones: EvohomeDevice[];
  }) => {
    setZones(data.zones || []);
  }, []);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress sx={{ color: ACCENT }} /></Box>;
  }

  const connected = Boolean(account?.connected);

  return (
    <Box sx={{ color: 'white', px: 0.5 }}>
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: ACCENT, mb: 1 }}>
        {t('settings.evohomeDiscovery.accountTitle', {
          defaultValue: 'Total Connect Comfort Intl',
        })}
      </Typography>

      {connected ? (
        <Box sx={{ border: '1px solid #333', borderRadius: 1, p: 1.5, mb: 1.5, bgcolor: '#2F3235' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>
              {t('settings.evohomeDiscovery.accountName', { defaultValue: 'Total Connect Comfort Intl' })}
            </Typography>
            <Chip
              size="small"
              label={t('settings.evohomeDiscovery.connected', { defaultValue: 'Conectada' })}
              sx={{ bgcolor: 'rgba(102,187,106,0.2)', color: '#66bb6a' }}
            />
          </Box>
          <Typography sx={{ fontSize: 12, color: '#aaa' }}>
            {t('settings.evohomeDiscovery.accountMeta', {
              defaultValue: 'usuario: {{user}}',
              user: account.username || '…',
            })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <Button size="small" variant="outlined" onClick={handleDisconnect} disabled={busy} sx={{ color: '#e57373', borderColor: '#e57373' }}>
              {t('settings.evohomeDiscovery.disconnect', { defaultValue: 'Desconectar' })}
            </Button>
            <Button size="small" variant="contained" onClick={handlePurge} disabled={busy} sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' } }}>
              {t('settings.evohomeDiscovery.purge', { defaultValue: 'Borrar' })}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          {account?.credentials_stored && (
            <Button size="small" variant="contained" onClick={handlePurge} disabled={busy} sx={{ bgcolor: '#c62828', mb: 1.5, '&:hover': { bgcolor: '#b71c1c' } }}>
              {t('settings.evohomeDiscovery.purge', { defaultValue: 'Borrar' })}
            </Button>
          )}
          <Typography sx={{ fontSize: 12, color: '#999', mb: 0.5 }}>
            {t('settings.evohomeDiscovery.loginHint', {
              defaultValue: 'Email y contraseña de Total Connect Comfort International:',
            })}
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="email@ejemplo.com"
            autoComplete="username"
            sx={{ mb: 1, input: { color: 'white' }, '& .MuiOutlinedInput-root': { bgcolor: '#1a1a1a' } }}
          />
          <TextField
            fullWidth
            size="small"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="current-password"
            sx={{ mb: 1, input: { color: 'white' }, '& .MuiOutlinedInput-root': { bgcolor: '#1a1a1a' } }}
          />
          <Button
            fullWidth
            variant="contained"
            onClick={handleConnect}
            disabled={busy || !username.trim() || !password}
            sx={{ bgcolor: ACCENT }}
          >
            {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('settings.evohomeDiscovery.connect', { defaultValue: 'Conectar' })}
          </Button>
        </Box>
      )}

      {connected && (
        <>
          <Button fullWidth variant="contained" onClick={handleSync} disabled={busy} sx={{ bgcolor: ACCENT, mb: 1.5 }}>
            {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('settings.evohomeDiscovery.sync', { defaultValue: 'Sincronizar' })}
          </Button>
          {syncResult && (
            <Typography sx={{ fontSize: 12, color: '#bbb', mb: 1 }}>
              +{syncResult.added ?? 0} · ↻{syncResult.updated ?? 0} · —{syncResult.already_known ?? 0} · ausentes {syncResult.missing ?? 0}
              {syncResult.errors?.length ? ` · errores ${syncResult.errors.length}` : ''}
            </Typography>
          )}
          {unassigned > 0 && onGoToDevices && (
            <Button size="small" onClick={onGoToDevices} sx={{ color: ACCENT, mb: 1.5 }}>
              {t('settings.evohomeDiscovery.goToDevices', {
                defaultValue: 'Ir a Dispositivos ({{count}} sin asignar)',
                count: unassigned,
              })}
            </Button>
          )}

          <EvohomeSystemControls
            key={systemControlsKey}
            isDemoUser={isDemoUser}
            onDemoActionAttempt={onDemoActionAttempt}
            selectedLocationId={selectedLocationId}
            onSelectedLocationIdChange={setSelectedLocationId}
            onOverviewChange={handleOverviewChange}
            showHeatSupplySettings
          />

          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#999', mb: 0.75, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {t('settings.evohomeDiscovery.zones', { defaultValue: 'Zonas' })}
            </Typography>
            {zones.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: '#777' }}>
                {t('settings.evohomeDiscovery.noZones', { defaultValue: 'Sin zonas. Sincronizá la cuenta.' })}
              </Typography>
            ) : (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 0.85,
              }}>
                {zones.map((z) => {
                  const modeLabel = formatEvohomeSetpointModeLabel(
                    t, z.setpoint_mode, z.until_ts,
                  );
                  return (
                    <Box
                      key={z.id}
                      onClick={() => onOpenZoneModal?.(z)}
                      sx={{
                        border: '1px solid #444',
                        borderRadius: 1.5,
                        bgcolor: '#2F3235',
                        p: 1.1,
                        cursor: onOpenZoneModal ? 'pointer' : 'default',
                        transition: 'border-color 160ms ease, background-color 160ms ease',
                        '&:active': { transform: 'scale(0.98)' },
                        '&:hover': { borderColor: ACCENT, bgcolor: '#35383c' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: '#fff',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          pr: 0.5,
                        }}>
                          {z.nombre}
                        </Typography>
                        <WhatshotIcon sx={{ fontSize: 14, color: HEAT, opacity: z.asking_heat ? 1 : 0.28, flexShrink: 0 }} />
                      </Box>
                      <Typography sx={{ fontSize: 12, color: '#ccc' }}>
                        {z.temperature != null ? Number(z.temperature).toFixed(1) : '—'}°
                        {' → '}
                        {z.setpoint != null ? Number(z.setpoint).toFixed(1) : '—'}°
                      </Typography>
                      {modeLabel && (
                        <Typography
                          sx={{
                            fontSize: 10,
                            color: evohomeSetpointModeColor(z.setpoint_mode),
                            mt: 0.35,
                            fontWeight: 600,
                          }}
                        >
                          {modeLabel}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </>
      )}

      {error && <Typography sx={{ color: '#e57373', fontSize: 12, mt: 1 }}>{String(error)}</Typography>}
    </Box>
  );
};

export default EvohomeDiscoveryTab;
